import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { getCardCompanyMailUrl } from '@/constant/approval';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher, postDataFetcher } from '@/utils/apis';
import type { DocumentStatus } from './types';

/** 카드사 메일 발송 대상 법인카드 품의 */
export const CARD_COMPANY_MAIL_TYPES: number[] = [204, 206, 207];

type DispatchRecord = {
  /** 권한 없음도 200 으로 줄 때의 발송 권한. 없으면 200 자체가 권한 있음 */
  canSend?: boolean;
  hasSent: boolean;
  lastSentAt: string | null;
};

const canSendOf = (record: DispatchRecord | undefined): boolean =>
  !!record && record.canSend !== false;

type Params = {
  documentId: string;
  apvType?: number;
  status?: DocumentStatus;
};

/**
 * 더보기 메뉴 "카드사 메일 발송" (레거시 js-send-card-email).
 * 발송 권한(지정 담당자·카드 담당 부서)은 서버만 알아서 발송 이력 조회 결과로 노출 — 권한 없음은 403 또는 canSend false
 */
const useCardCompanyMail = ({ documentId, apvType, status }: Params) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const { showSuccess, showError } = useSnackbar();
  const [isSending, setIsSending] = useState(false);

  const eligible =
    apvType !== undefined &&
    CARD_COMPANY_MAIL_TYPES.includes(apvType) &&
    (status === 'P' || status === 'C');
  const url = eligible ? getCardCompanyMailUrl(documentId) : null;

  const { data, mutate } = useSWR<DispatchRecord>(url, getNestedDataFetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  const send = useCallback(async () => {
    if (!url) return;
    setIsSending(true);
    try {
      await postDataFetcher(url, {});
      showSuccess({ message: t('카드사 메일이 발송되었습니다.') });
      await mutate().catch(() => undefined);
    } catch {
      showError({ message: t('메일 발송에 실패했습니다.') });
    } finally {
      setIsSending(false);
    }
  }, [url, mutate, showSuccess, showError, t]);

  const sendMail = useCallback(async () => {
    if (!url || isSending) return;
    let record: DispatchRecord | undefined;
    try {
      record = await mutate();
    } catch {
      record = undefined;
    }
    if (!record) {
      showError({ message: t('발송 이력을 확인할 수 없습니다.') });
      return;
    }
    if (!canSendOf(record)) {
      showError({ message: t('카드사 메일 발송 권한이 없습니다.') });
      return;
    }
    dialog.show({
      type: 'confirm',
      message: record.hasSent
        ? t(
            '메일을 발송한 기록이 있습니다. (발송 일시: {{sentAt}})\n중복 발송일 수 있으니 확인 후 진행해 주세요.',
            { sentAt: record.lastSentAt ?? '' },
          )
        : t('카드사 담당팀에 메일을 발송하시겠습니까?'),
      onConfirm: () => void send(),
    });
  }, [url, isSending, mutate, dialog, send, showError, t]);

  return {
    canSendMail: canSendOf(data),
    sendMailDisabled: isSending,
    sendMail,
  };
};

export default useCardCompanyMail;
